import { FC, useEffect, useState } from 'react';
import { Form } from 'antd';
import { InputText, Modal, Select, Textarea } from '@g2u/ui-kit';
import { getTicketTypes } from 'queries/tickets/getTicketTypes';
import { Condition, type JsonDataItem } from 'types';
import { parseErrors, Toast, useJob, validateFormSilently } from 'helpers';
import { useQuery } from '@tanstack/react-query';
import { useCreateTicket } from 'helpers/hooks/useCreateTicket';
import { useTechnician } from 'helpers/hooks/useTechnician';

type Props = {
  jobId?: string;
  customerId?: string;
  technicianId?: string;
  isOpen: boolean;
  onClose: () => void;
};

type Fields = {
  type: number;
  description: string;
  technician: string;
  techVisible?: Condition;
};

export const CreateTicketModal: FC<Props> = ({ jobId, customerId, technicianId, isOpen, onClose }) => {
  const [form] = Form.useForm<Fields>();

  const { data: ticketTypes, isInitialLoading: isLoadingTypes } = useQuery(['ticket-types'], getTicketTypes, {
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const values = Form.useWatch([], form);

  const [isSubmittable, setIsSubmittable] = useState(true);

  useEffect(() => {
    validateFormSilently(form, setIsSubmittable);
  }, [form, values]);

  const { job } = useJob(jobId);
  const { technician } = useTechnician(technicianId);

  const technicianName = technician
    ? `${technician?.contact1_firstname} ${technician?.contact1_surname}`
    : job?.products?.[0]?.service_technicians?.[0]?.technician?.name
      ? job?.products?.[0]?.service_technicians?.[0]?.technician?.name
      : 'N/A';

  const { createTicket, isLoading } = useCreateTicket();

  const finishHandler = async () => {
    await form.validateFields();

    const { description, techVisible } = form.getFieldsValue();
    await createTicket({
      ticketTypeId: values.type,
      description,
      customerId,
      jobId,
      technicianId,
      techVisible,
    });

    form.resetFields();
    onClose();
  };

  return (
    <Modal.$
      dataTestId="create-ticket-modal"
      open={isOpen}
      title="New Ticket"
      okButtonProps={{ disabled: !isSubmittable }}
      onCancel={onClose}
      onOk={form.submit}
      confirmLoading={isLoading}
      cancelButtonProps={{ disabled: isLoading }}
    >
      <Form
        form={form}
        onFinish={finishHandler}
        labelCol={{ style: { width: 130 } }}
        labelAlign="left"
        layout="horizontal"
      >
        <Select.$
          name="type"
          label="Ticket Type"
          rules={[{ required: true }]}
          loading={isLoadingTypes}
          options={ticketTypes?.map(({ ID, desc }) => ({
            value: ID,
            label: desc,
          }))}
          onSelect={(newValue) => {
            const selectedType = ticketTypes?.find(({ ID }) => ID === newValue);
            if (newValue !== values.type) {
              form.setFieldValue('techVisible', selectedType?.default_tech_visibility ? Condition.Yes : Condition.No);
              form.setFieldValue('description', selectedType?.default_description);
            }
          }}
        />
        <Textarea.$
          name="description"
          label="Ticket Description"
          rules={[{ required: true }]}
          disabled={isLoadingTypes}
        />
        <InputText.$ name="technician" disabled label="Technician" initialValue={technicianName} />

        <Select.$
          name="techVisible"
          label="Tech Visible"
          rules={[{ required: true }]}
          options={[
            { value: Condition.Yes, label: 'Yes' },
            { value: Condition.No, label: 'No' },
          ]}
          disabled={isLoadingTypes}
        />
      </Form>
    </Modal.$>
  );
};
